import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/api';

const fmtQty = (q) => {
  const n = Number(q);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 4 });
};

const DISPLAY_TZ = 'Asia/Kolkata';

/** Prefer ISO strings with Z or +05:30 from API; always display in India time. Set API_NAIVE_MYSQL_DATETIME_AS on server when needed. */
const fmtCreated = (s) => {
  if (!s) return '—';
  try {
    const t = String(s).trim();
    const iso = t.includes('T') ? t : t.replace(' ', 'T');
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: DISPLAY_TZ,
    });
  } catch {
    return s;
  }
};

const PAGE_OPTIONS = [10, 25, 50, 100];
const MODAL_ROWS_PER_SECTION = 30;

function ItemQtyTableHead() {
  return (
    <thead>
      <tr className="bg-sap-headerFrom text-white">
        <th className="text-left px-3 py-2 font-semibold text-[10px] uppercase border-b border-white/10">Item no.</th>
        <th className="text-left px-3 py-2 font-semibold text-[10px] uppercase border-b border-white/10">Description</th>
        <th className="text-right px-3 py-2 font-semibold text-[10px] uppercase border-b border-white/10">Quantity</th>
      </tr>
    </thead>
  );
}

/**
 * One ordered item, and what it is made of.
 *
 * The list showed the finished item on its own, which for a machine is a single line
 * standing in for everything that goes into it. Its components sit underneath, folded
 * away: a BOM can run to dozens of lines and opening every one at once would bury the
 * items actually ordered.
 */
function ItemQtyTableRow({ it, stripe, bom }) {
  const [open, setOpen] = useState(false);
  const comps = bom?.components || [];
  const hasBom = comps.length > 0;

  return (
    <>
      <tr className={`border-b border-sap-border/20 ${stripe ? 'bg-sap-bg/30' : 'bg-white'}`}>
        <td className="px-3 py-2 font-mono font-medium text-sap-blue">
          {hasBom ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 hover:underline"
              title={open ? 'Hide what this is made of' : 'Show what this is made of'}
            >
              <span className={`inline-block transition-transform text-sap-textMuted ${open ? 'rotate-90' : ''}`}>›</span>
              {it.item_no}
            </button>
          ) : it.item_no}
        </td>
        <td className="px-3 py-2 text-sap-text">
          {it.description || '—'}
          {hasBom && (
            <span className="ml-2 text-2xs text-sap-textMuted">
              BOM · {comps.length} component{comps.length === 1 ? '' : 's'}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtQty(it.quantity)}</td>
      </tr>

      {open && hasBom && (
        <tr className="border-b border-sap-border/20 bg-sap-blue/[0.03]">
          <td colSpan={3} className="px-3 py-2">
            <div className="text-2xs text-sap-textMuted mb-1.5">
              Components of {it.item_no}
              {bom.bom_qty && bom.bom_qty !== 1 ? ` (BOM makes ${fmtQty(bom.bom_qty)})` : ''}
              {bom.last_touched_at && (
                <span className="ml-2 text-amber-700">
                  · BOM last updated {new Date(bom.last_touched_at).toLocaleString()}
                </span>
              )}
            </div>
            <table className="w-full text-2xs border-collapse">
              <tbody>
                {comps.map((c, ci) => (
                  <tr key={`c-${it.item_no}-${ci}`} className="border-t border-sap-border/20">
                    <td className="py-1 pr-3 font-mono text-sap-text w-40">{c.item_no}</td>
                    <td className="py-1 pr-3 text-sap-textLight">
                      {c.description || '—'}
                      {c.line_type && c.line_type !== 'Item' && (
                        <span className="ml-1 text-sap-textMuted">({c.line_type})</span>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums w-24">{fmtQty(c.quantity)}</td>
                    <td className="py-1 text-sap-textMuted w-14">{c.uom || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function ModalSectionPager({ sectionLabel, page, totalItems, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / MODAL_ROWS_PER_SECTION));
  const fromIdx = totalItems === 0 ? 0 : (page - 1) * MODAL_ROWS_PER_SECTION + 1;
  const toIdx = totalItems === 0 ? 0 : Math.min(page * MODAL_ROWS_PER_SECTION, totalItems);

  useEffect(() => {
    if (page > totalPages) onPageChange(totalPages);
  }, [page, totalPages, onPageChange]);

  if (totalItems <= MODAL_ROWS_PER_SECTION) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-2 px-1 text-2xs text-sap-textLight">
      <span>
        {sectionLabel}: <strong className="text-sap-text tabular-nums">{fromIdx}</strong>
        {' \u2013 '}
        <strong className="text-sap-text tabular-nums">{toIdx}</strong> of{' '}
        <strong className="text-sap-text tabular-nums">{totalItems}</strong>
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="h-7 px-2 font-semibold border border-sap-border rounded bg-white hover:bg-sap-bg disabled:opacity-40"
        >
          Prev
        </button>
        <span className="tabular-nums px-1 min-w-[3.5rem] text-center text-sap-text">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="h-7 px-2 font-semibold border border-sap-border rounded bg-white hover:bg-sap-bg disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function groupChangesByTime(changes) {
  if (!changes || !changes.length) return [];
  const map = new Map();
  for (const c of changes) {
    const key = c.changed_at || 'unknown';
    if (!map.has(key)) {
      map.set(key, { changed_at: c.changed_at, changed_by: c.changed_by, so_number: c.so_number, added: [], deleted: [] });
    }
    const g = map.get(key);
    if (!g.changed_by && c.changed_by) g.changed_by = c.changed_by;
    if (!g.so_number && c.so_number) g.so_number = c.so_number;
    if (c.change_type === 'added') g.added.push(c);
    else if (c.change_type === 'deleted') g.deleted.push(c);
  }
  return [...map.values()];
}

/** Counts from the most recent change batch (matches change history order). */
function latestEditItemSummary(changes, currentItemCount) {
  if (!changes?.length || currentItemCount == null || Number.isNaN(Number(currentItemCount))) return null;
  const groups = groupChangesByTime(changes);
  const g = groups[0];
  if (!g) return null;
  const added = g.added.length;
  const removed = g.deleted.length;
  if (!added && !removed) return null;
  const beforeApprox = Math.max(0, Number(currentItemCount) + removed - added);
  return { added, removed, beforeApprox };
}

function ProjectItemsModalBody({ detail, detailLoading, detailErr }) {
  const [itemsPage, setItemsPage] = useState(1);

  const allItems = useMemo(() => (detail?.items || []), [detail]);

  useEffect(() => {
    setItemsPage(1);
  }, [detail?.project_code, detail?.item_count]);

  const itemsSlice = useMemo(() => {
    const start = (itemsPage - 1) * MODAL_ROWS_PER_SECTION;
    return allItems.slice(start, start + MODAL_ROWS_PER_SECTION);
  }, [allItems, itemsPage]);

  const changeGroups = useMemo(
    () => groupChangesByTime(detail?.changes || []),
    [detail?.changes]
  );

  if (detailLoading) {
    return <div className="py-12 text-center text-xs text-sap-textLight">Loading items...</div>;
  }
  if (detailErr) {
    return <div className="m-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{detailErr}</div>;
  }
  if (!detail) {
    return null;
  }

  const n = allItems.length;

  return (
    <div className="pb-2">
      <div className="px-3 mt-2">
        <div className="text-2xs font-bold uppercase tracking-wider text-sap-text mb-1.5">
          Current items ({n})
        </div>
        <ModalSectionPager
          sectionLabel="Items"
          page={itemsPage}
          totalItems={n}
          onPageChange={setItemsPage}
        />
        <table className="w-full text-xs border-collapse rounded-lg overflow-hidden border border-sap-border/50">
          <ItemQtyTableHead />
          <tbody>
            {n === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-sap-textLight bg-sap-bg/20">
                  No line quantities found for this project.
                </td>
              </tr>
            ) : (
              itemsSlice.map((it, j) => (
                <ItemQtyTableRow
                  key={`item-${String(it.item_no)}-${itemsPage}-${j}`}
                  it={it}
                  stripe={j % 2 === 1}
                  bom={detail?.boms?.[it.item_no]}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {changeGroups.length > 0 && (
        <div className="mt-5 mx-3 pt-4 border-t-2 border-dashed border-sap-border/60">
          <div className="text-2xs font-bold uppercase tracking-wider text-sap-text mb-3">
            Change History
          </div>
          <div className="space-y-3">
            {changeGroups.map((g, gi) => {
              const addedCount = g.added.length;
              const deletedCount = g.deleted.length;
              return (
                <div key={`cg-${gi}`} className="rounded-lg border border-sap-border/50 overflow-hidden">
                  <div className="px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200/60">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <span className="font-semibold text-amber-800">Modified</span>
                      {g.changed_at && (
                        <span className="text-amber-700">
                          on <span className="font-medium text-amber-900">{fmtCreated(g.changed_at)}</span>
                        </span>
                      )}
                      {g.changed_by && (
                        <span className="text-amber-700">
                          by <span className="font-semibold text-amber-900">{g.changed_by}</span>
                        </span>
                      )}
                      {g.so_number && (
                        <span className="text-amber-600 text-2xs">
                          (SO <span className="font-mono font-medium">{g.so_number}</span>)
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 text-2xs">
                      {deletedCount > 0 && (
                        <span className="text-red-700 font-medium">
                          Deleted {deletedCount} item{deletedCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {addedCount > 0 && (
                        <span className="text-green-700 font-medium">
                          Added {addedCount} item{addedCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {deletedCount > 0 && (
                    <div className="bg-red-50/40">
                      <div className="px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-red-700">
                        Deleted items
                      </div>
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-red-100/50">
                            <th className="text-left px-3 py-1.5 font-semibold text-[10px] uppercase text-red-800">Item no.</th>
                            <th className="text-left px-3 py-1.5 font-semibold text-[10px] uppercase text-red-800">Description</th>
                            <th className="text-right px-3 py-1.5 font-semibold text-[10px] uppercase text-red-800">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.deleted.map((c, ci) => (
                            <tr key={`d-${gi}-${ci}`} className={`border-b border-red-100 ${ci % 2 === 1 ? 'bg-red-50/30' : ''}`}>
                              <td className="px-3 py-1.5 font-mono font-medium text-red-700 line-through">{c.item_no}</td>
                              <td className="px-3 py-1.5 text-red-600">{c.item_description || '---'}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-red-600">{fmtQty(c.quantity)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {addedCount > 0 && (
                    <div className="bg-green-50/40">
                      <div className="px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-green-700">
                        Added items
                      </div>
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-green-100/50">
                            <th className="text-left px-3 py-1.5 font-semibold text-[10px] uppercase text-green-800">Item no.</th>
                            <th className="text-left px-3 py-1.5 font-semibold text-[10px] uppercase text-green-800">Description</th>
                            <th className="text-right px-3 py-1.5 font-semibold text-[10px] uppercase text-green-800">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.added.map((c, ci) => (
                            <tr key={`a-${gi}-${ci}`} className={`border-b border-green-100 ${ci % 2 === 1 ? 'bg-green-50/30' : ''}`}>
                              <td className="px-3 py-1.5 font-mono font-medium text-green-700">{c.item_no}</td>
                              <td className="px-3 py-1.5 text-green-600">{c.item_description || '---'}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-green-600">{fmtQty(c.quantity)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TotalProjects() {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modalProject, setModalProject] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search: search.trim(),
      });
      const r = await api.get(`/projects/report?${params}`);
      setRows(Array.isArray(r?.rows) ? r.rows : []);
      setTotal(Number(r?.total) || 0);
    } catch (e) {
      setErr(e.message || 'Failed to load projects');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, limit]);

  useEffect(() => {
    const t = setTimeout(loadList, 250);
    return () => clearTimeout(t);
  }, [loadList]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const fromIdx = total === 0 ? 0 : (page - 1) * limit + 1;
  const toIdx = total === 0 ? 0 : Math.min(page * limit, total);

  const openProject = async (project_code, project_name) => {
    setModalProject({ project_code, project_name });
    setDetail(null);
    setDetailErr('');
    setDetailLoading(true);
    try {
      const d = await api.get(`/projects/items-usage?code=${encodeURIComponent(project_code)}`);
      setDetail(d);
    } catch (e) {
      setDetailErr(e.message || 'Failed to load items');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = useCallback(() => {
    setModalProject(null);
    setDetail(null);
    setDetailErr('');
  }, []);

  useEffect(() => {
    if (!modalProject) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalProject, closeModal]);

  useEffect(() => {
    const maxP = Math.max(1, Math.ceil(total / limit) || 1);
    if (total > 0 && page > maxP) setPage(maxP);
  }, [total, limit, page]);

  const modalItemSummary = useMemo(
    () => latestEditItemSummary(detail?.changes, detail?.item_count),
    [detail?.changes, detail?.item_count]
  );

  return (
    <div className="space-y-4 pb-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-sap-text tracking-tight">Total Projects</h1>
          <p className="text-2xs text-sap-textLight mt-1 max-w-xl">
            Newest by <strong className="font-medium text-sap-text/80">latest sales order created</strong> on the project first.
            Click a row for current items and change history.
          </p>
        </div>
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search code or name..."
          className="w-64 border border-sap-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue shadow-sm"
        />
      </div>

      {err && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{err}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-sap-border/60 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-gradient-to-r from-sap-blue/8 to-transparent border-b border-sap-border/50">
          <span className="text-xs text-sap-text">
            {loading ? (
              <span className="text-sap-textLight">Loading...</span>
            ) : (
              <>
                <strong className="text-sap-text tabular-nums">{total.toLocaleString('en-IN')}</strong>
                <span className="text-sap-textLight"> projects</span>
                {total > 0 && (
                  <span className="text-sap-textLight">
                    {' '}showing <strong className="text-sap-text">{fromIdx}</strong> - <strong className="text-sap-text">{toIdx}</strong>
                  </span>
                )}
              </>
            )}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-2xs text-sap-textLight">
              Per page
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="h-8 px-2 text-xs border border-sap-border rounded-md bg-white focus:outline-none focus:border-sap-blue"
              >
                {PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="h-8 px-2.5 text-2xs font-semibold border border-sap-border rounded-md bg-white hover:bg-sap-bg disabled:opacity-40"
              >
                Prev
              </button>
              <span className="text-2xs text-sap-text tabular-nums px-1 min-w-[4.5rem] text-center">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="h-8 px-2.5 text-2xs font-semibold border border-sap-border rounded-md bg-white hover:bg-sap-bg disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-sap-headerFrom text-white">
                <th className="text-left px-3 py-2.5 font-semibold text-[10px] uppercase tracking-wider w-10 border-b border-white/10">#</th>
                <th className="text-left px-3 py-2.5 font-semibold text-[10px] uppercase tracking-wider border-b border-white/10 min-w-[9rem]">Project code</th>
                <th className="text-left px-3 py-2.5 font-semibold text-[10px] uppercase tracking-wider border-b border-white/10">Project name</th>
                <th className="text-left px-3 py-2.5 font-semibold text-[10px] uppercase tracking-wider w-44 border-b border-white/10">First project / SO</th>
                {/* <th className="text-left px-3 py-2.5 font-semibold text-[10px] uppercase tracking-wider w-44 border-b border-white/10 whitespace-nowrap">Latest SO created</th> */}
              </tr>
            </thead>
            <tbody className="text-sap-text">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-3 py-14 text-center text-sap-textLight bg-sap-bg/30">Loading...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-14 text-center text-sap-textLight bg-sap-bg/20">No projects match your search.</td>
                </tr>
              ) : (
                rows.map((r, i) => {
                  const rowNum = (page - 1) * limit + i + 1;
                  const zebra = i % 2 === 0 ? 'bg-white' : 'bg-sap-bg/40';
                  return (
                    <tr
                      key={`${r.project_code}-${rowNum}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openProject(r.project_code, r.project_name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openProject(r.project_code, r.project_name);
                        }
                      }}
                      className={`border-b border-sap-border/30 ${zebra} hover:bg-sap-blue/10 cursor-pointer transition-colors`}
                    >
                      <td className="px-3 py-2.5 text-sap-textLight tabular-nums">{rowNum}</td>
                      <td className="px-3 py-2.5 font-mono font-semibold text-sap-blue">{r.project_code}</td>
                      <td className="px-3 py-2.5 text-sap-text">{r.project_name || '---'}</td>
                      <td className="px-3 py-2.5 text-sap-textLight whitespace-nowrap">
                        {fmtCreated(r.project_created ?? r.last_activity)}
                      </td>
                      {/* <td className="px-3 py-2.5 text-sap-text whitespace-nowrap">
                        {fmtCreated(r.last_sales_order_at)}
                      </td> */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalProject && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-sap-border w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="total-proj-modal-title"
          >
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-sap-border bg-gradient-to-r from-sap-blue/10 to-transparent">
              <div className="min-w-0">
                <h2 id="total-proj-modal-title" className="text-sm font-semibold text-sap-text truncate">
                  Items — {modalProject.project_code}
                </h2>
                <p className="text-xs font-semibold text-sap-text mt-1 tabular-nums">
                  Total {detail?.item_count != null ? `${detail.item_count} distinct item${detail.item_count !== 1 ? 's' : ''}` : detailLoading ? '...' : '—'}
                  {modalItemSummary && (
                    <>
                      <span className="font-normal text-sap-textLight">
                        {' '}
                        (~{modalItemSummary.beforeApprox} before last edit + {modalItemSummary.added} added
                        {modalItemSummary.removed > 0 ? ` − ${modalItemSummary.removed} removed` : ''})
                      </span>
                    </>
                  )}
                </p>
                <p className="text-2xs text-sap-textLight">
                  <span className="truncate block">
                    {modalProject.project_name || detail?.project_name || '—'}
                    {detail?.changes?.length > 0 && (
                      <span className="ml-1 text-amber-700 font-medium">
                        · {detail.changes.length} line change{detail.changes.length !== 1 ? 's' : ''} logged
                      </span>
                    )}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="shrink-0 px-3 py-1 text-xs font-medium text-sap-text border border-sap-border rounded-lg hover:bg-sap-bg"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-auto min-h-[200px]">
              <ProjectItemsModalBody detail={detail} detailLoading={detailLoading} detailErr={detailErr} />
            </div>
            <div className="px-4 py-2 border-t border-sap-border bg-sap-bg/40 text-2xs text-sap-textLight">
              Current items from live SO lines and production orders. Click an item to see
              its BOM components. Change History tracks items added or deleted when a sales
              order is updated (date, user, item details). BOM changes show the date they
              were last applied; what changed inside a BOM, and by whom, is not recorded
              yet — most BOM edits are made in SAP.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
